import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PROMPTS } from "@/constants/testIds";
import { Save, RotateCcw, ScrollText, GitBranch } from "lucide-react";

function fmtWhen(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

export default function PromptsPage() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState(null);
    const [draft, setDraft] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [saved, setSaved] = useState(false);

    const load = async () => {
        try {
            const res = await api.get("/prompts");
            setData(res.data);
            if (!tab && res.data.agents?.length) setTab(res.data.agents[0].id);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When switching tab, load either the active override or the hard default
    useEffect(() => {
        if (!data || !tab) return;
        const active = data.active_overrides?.[tab];
        setDraft(active !== undefined ? active : data.defaults[tab] || "");
        setNote("");
        setErr(null);
    }, [tab, data]);

    const currentAgent = useMemo(
        () => (data && tab ? data.agents.find((a) => a.id === tab) : null),
        [data, tab],
    );

    const savePrompt = async () => {
        if (!tab) return;
        setBusy(true);
        setErr(null);
        try {
            await api.post("/prompts", {
                agent_id: tab,
                system_prompt: draft,
                note,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 1500);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const revert = async () => {
        if (!tab) return;
        setBusy(true);
        try {
            await api.post(`/prompts/${tab}/revert`);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 text-center text-sm text-[color:var(--text-muted)] font-light">
                Loading prompt library…
            </div>
        );
    }

    const history = (data.history || []).filter((p) => p.agent_id === tab);

    return (
        <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 lg:py-10 space-y-8">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                    Admin · Prompt Library
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--text-main)] leading-tight mt-1">
                    Versioned Agent System Prompts
                </h2>
                <p className="text-sm text-[color:var(--text-muted)] font-light mt-2 max-w-2xl">
                    Edit any agent&rsquo;s system prompt. Saving creates a new immutable version
                    that becomes active. Prior versions are kept and can be inspected in
                    the history panel. Revert to fall back to the hardcoded default.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-[color:var(--border-subtle)] pb-2">
                {data.agents.map((a) => {
                    const overridden = data.active_overrides[a.id] !== undefined;
                    return (
                        <button
                            key={a.id}
                            data-testid={PROMPTS.agentTab(a.id)}
                            onClick={() => setTab(a.id)}
                            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm transition-colors ${
                                tab === a.id
                                    ? "bg-[color:var(--profecia-gold-light)] text-[color:var(--profecia-gold)]"
                                    : "text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-hover)]"
                            }`}
                        >
                            {a.name}
                            {overridden && (
                                <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[color:var(--profecia-gold)] text-white">
                                    custom
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2">
                    {String(err)}
                </div>
            )}

            {currentAgent && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <section className="lg:col-span-2 bg-white rounded-lg border border-[color:var(--border-subtle)] card-ambient p-6">
                        <div className="flex items-baseline justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[color:var(--profecia-gold)]">
                                    Editing
                                </div>
                                <h3 className="font-serif text-2xl text-[color:var(--text-main)] leading-none mt-1">
                                    {currentAgent.name}
                                </h3>
                                <div className="text-xs text-[color:var(--text-muted)] font-light mt-1">
                                    {currentAgent.desc}
                                </div>
                            </div>
                            <ScrollText size={16} className="text-[color:var(--profecia-gold)]" />
                        </div>

                        <textarea
                            data-testid={PROMPTS.textarea}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            rows={22}
                            className="w-full resize-y rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-hover)] p-4 font-mono text-[13px] leading-relaxed text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--profecia-gold)] focus:bg-white transition-colors"
                        />
                        <div className="mt-3 flex flex-col md:flex-row gap-3 md:items-center">
                            <input
                                data-testid={PROMPTS.noteInput}
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Optional change note (e.g., 'tighter tone', 'more brand-safe language')"
                                className="flex-1 px-3 py-2.5 rounded-md border border-[color:var(--border-subtle)] focus:outline-none focus:border-[color:var(--profecia-gold)] text-sm"
                            />
                            <div className="flex gap-2">
                                <button
                                    data-testid={PROMPTS.revertBtn}
                                    onClick={revert}
                                    disabled={busy || data.active_overrides[tab] === undefined}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-hover)] disabled:opacity-40 transition-colors"
                                >
                                    <RotateCcw size={14} /> Revert to default
                                </button>
                                <button
                                    data-testid={PROMPTS.saveBtn}
                                    onClick={savePrompt}
                                    disabled={busy || !draft.trim()}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[color:var(--profecia-gold)] text-white text-sm font-medium hover:bg-[color:var(--profecia-gold-hover)] disabled:opacity-50 transition-colors"
                                >
                                    <Save size={14} /> {saved ? "Saved ✓" : "Save new version"}
                                </button>
                            </div>
                        </div>
                    </section>

                    <aside className="bg-[color:var(--surface-hover)] rounded-lg border border-[color:var(--border-subtle)] p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <GitBranch size={14} className="text-[color:var(--profecia-gold)]" />
                            <h4 className="font-serif text-lg text-[color:var(--text-main)] leading-none">
                                Version History
                            </h4>
                        </div>
                        {history.length === 0 ? (
                            <div className="text-xs text-[color:var(--text-muted)] font-light py-6 text-center">
                                No custom versions yet — the hardcoded default is in use.
                            </div>
                        ) : (
                            <ul className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                                {history.map((v) => (
                                    <li
                                        key={v.id}
                                        data-testid={PROMPTS.versionRow(v.id)}
                                        className="text-xs bg-white rounded-md border border-[color:var(--border-subtle)] p-3"
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--profecia-gold)]">
                                                {v.active ? "active" : "archived"}
                                            </div>
                                            <div className="text-[10px] font-mono text-[color:var(--text-muted)]">
                                                {fmtWhen(v.created_at)}
                                            </div>
                                        </div>
                                        {v.note && (
                                            <div className="text-[color:var(--text-main)] mt-1">{v.note}</div>
                                        )}
                                        <div className="text-[10px] text-[color:var(--text-muted)] mt-1 font-mono truncate">
                                            by {v.author}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </aside>
                </div>
            )}
        </main>
    );
}
